// A compare's page (docs/design.md "Compares"): the store the compare view
// and the range views read, a `BranchHost` (state/rangeHost.ts) with no
// conversation. It reads the compare (state/compare/fetch.ts), makes its
// track (logic/compareTrack.ts), and keeps beside them what the page shows
// of it: the open pull request from the head, the head's checks and their
// annotations, `.gitattributes` at the head, and (on demand) each commit's
// pull requests. Marks and notes are the repository's, as on a pull
// request's page: the notes store is the one every page of the repository
// shares (`acquireNotes`), and the marks store the repository's marks,
// found by content (`MarksStore` with no pull request number), so a mark
// made on a pull request shows here on the same lines.
//
// Live updates: as the pull request's page does (state/pr.svelte.ts), it
// listens on the repository's and the user's topics (api/sse.ts
// LiveEvents): window focus, a reconnect or a resync reads the compare
// again (focus at most once a minute, `shouldRefetch`), through ETag
// revalidation, so an unchanged compare costs no request GitHub counts. A
// new head replaces the track and the files in place; the diff on screen
// is the old head's until the view reloads it (`headMoved` on the page).
// Notes and marks notices of the repository reload those; a checks notice
// for the head reads its checks again. Nothing polls. A branch deleted
// while its page is open (merged and cleaned up) leaves what was read
// shown, saying so (`gone`).
import { contentAt, restAll } from '../api/gh';
import { installUrl } from '../api/errors';
import { failureOf, type Failure } from '../api/failure';
import { onIdentityChange } from '../api/identity';
import type { CheckContext, PrFile } from '../api/queries';
import { LiveEvents, pausedForRateLimit, shouldRefetch, type Notice, type Trigger } from '../api/sse';
import type { LoadView } from '../loading/steps';
import { oneStep } from '../loading/steps';
import { generatedMatcher } from '../logic/categorize';
import { compareTrack } from '../logic/compareTrack';
import { emptyTrack, type Track } from '../logic/track';
import { compareHead, subjectLabel, type CompareSubject } from '../subject';
import { fetchLinkedPulls, headPulls, loadCompare, CompareNotFound, type HeadPull, type LinkedPull, type LoadedCompare } from './compare/fetch';
import { MarksStore, type MarksNotice } from './marks.svelte';
import { acquireNotes, type NotesStore } from './notes.svelte';
import { commitRollup, type CheckAnnotation } from './pr/fetch';
import { contextFails } from '../logic/checks';
import { PrPrefs } from './prefs.svelte';
import type { BranchHost, FilesSoFar } from './rangeHost';
import { session } from './session.svelte';

/**
 * The head's checks: GitHub's rollup of its check runs and commit statuses
 * together (COMMIT_CHECKS, as the relay's `checks` reads the same commit),
 * read with `logic/checks.ts`. `truncated`: more contexts than were read.
 */
export interface HeadChecks {
  contexts: readonly CheckContext[];
  truncated: boolean;
}

/** The most check runs at the head whose annotations are read (failing first). */
const MAX_ANNOTATED_RUNS = 10;

/** What a compare's store reads of GitHub: the page's own reads by default; tests give fakes. */
export interface CompareWorld {
  load(subject: CompareSubject, signal: AbortSignal): Promise<LoadedCompare>;
  pulls(subject: CompareSubject, base: string, signal: AbortSignal): Promise<HeadPull[]>;
  checks(subject: CompareSubject, head: string, signal: AbortSignal): Promise<HeadChecks>;
  annotations(subject: CompareSubject, run: CheckContext, signal: AbortSignal): Promise<CheckAnnotation[]>;
  gitattributes(subject: CompareSubject, head: string, signal: AbortSignal): Promise<string | null>;
  linked(subject: CompareSubject, shas: readonly string[], signal: AbortSignal): Promise<Map<string, LinkedPull[]>>;
  /** Listens for notices and triggers (LiveEvents); returns how to stop. */
  listen(topics: string[], handler: (topic: string, notice: Notice | null) => void): () => void;
}

const opts = (signal: AbortSignal) => ({ priority: 'visible' as const, signal });

export const GITHUB_WORLD: CompareWorld = {
  load: (subject, signal) => loadCompare(subject, opts(signal)),
  pulls: (subject, base, signal) => headPulls(subject, base, { priority: 'background', signal }),
  checks: async (subject, head, signal) => {
    const got = await commitRollup(subject.owner, subject.repo, head, { priority: 'background', signal });
    return { contexts: got?.contexts?.nodes ?? [], truncated: !!got?.contexts?.pageInfo?.hasNextPage };
  },
  annotations: async (subject, run, signal) =>
    (await restAll<Omit<CheckAnnotation, 'check'>>(`repos/${subject.owner}/${subject.repo}/check-runs/${run.databaseId}/annotations?per_page=100`, { priority: 'background', signal })).map((a) => ({ ...a, check: run.name ?? 'check' })),
  gitattributes: (subject, head, signal) => contentAt(subject.owner, subject.repo, '.gitattributes', head, { signal, priority: 'background' }),
  linked: (subject, shas, signal) => fetchLinkedPulls(subject.owner, subject.repo, shas, { priority: 'background', signal }),
  listen: (topics, handler) => {
    const live = new LiveEvents(topics, handler);
    return () => live.close();
  },
};

/** The page of compare `subject`: see the file comment. */
export class CompareStore implements BranchHost {
  readonly subject: CompareSubject;
  readonly conversation = null;
  /** Where the page is: reading the compare, showing it, GitHub has none (`notFound` says why), or the read failed (`failure`). */
  status: 'loading' | 'ready' | 'notfound' | 'error' = $state('loading');
  failure: Failure | null = $state(null);
  notFound: string | null = $state(null);
  /** GitHub no longer has the compare shown (its branch deleted since it was read): why, while what was read stays shown; null while it has. */
  gone: string | null = $state(null);
  /** The compare as last read. */
  compare: LoadedCompare | null = $state.raw(null);
  readonly track: Track = $derived(this.compare ? compareTrack(this.compare) : emptyTrack());
  /** The open pull requests from the head, the one into the base first; null until read (or when the read failed). */
  pulls: HeadPull[] | null = $state.raw(null);
  /** The head's checks; null until read. */
  checks: HeadChecks | null = $state.raw(null);
  annotations: Readonly<Record<string, CheckAnnotation[]>> = $state.raw({});
  generated: (path: string) => boolean = $state.raw(() => false);
  /** Each commit's pull requests (the linked pulls facet), once asked for (`loadLinked`); a commit not asked about is absent. */
  linked: ReadonlyMap<string, LinkedPull[]> = $state.raw(new Map());
  /** Why the linked pull requests could not be read; null while they could. */
  linkedError: string | null = $state(null);
  readonly prefs: PrPrefs;
  readonly marks: MarksStore;
  readonly notes: NotesStore;
  /** The server keeps nothing for this repository (no installation it knows covers it): notes and marks are off. */
  unrecorded = $state(false);
  readonly closed = false;
  readonly filesSoFar: FilesSoFar | null = null;
  /** The head the diff on screen was built at (new notes are read there), while the Files view shows one. */
  diffHead: string | null = $state(null);
  private diffOwner: object | null = null;
  private loading: Promise<void> | null = null;
  private lastLoadAt = 0;
  private abort = new AbortController();
  private linkedFor: string | null = null;
  private stop: () => void = () => {};
  private releaseNotes: () => void;
  private unwatch: () => void;
  private disposed = false;

  constructor(
    subject: CompareSubject,
    private world: CompareWorld = GITHUB_WORLD,
  ) {
    this.subject = subject;
    this.prefs = new PrPrefs(subject);
    // The repository's marks, found by the content the view shows (no pull request number).
    this.marks = new MarksStore(subject.owner, subject.repo, null, () => (this.unrecorded = true));
    ({ store: this.notes, release: this.releaseNotes } = acquireNotes(subject.owner, subject.repo, () => (this.unrecorded = true)));
    // Another account signed in: nothing read for this one is shown to it.
    this.unwatch = onIdentityChange(() => this.dispose());
  }

  /** The files changed from the merge base to the head; null until read, or when the read failed. */
  get files(): readonly PrFile[] | null {
    return this.compare?.files ?? null;
  }
  get filesHead(): string | null {
    return this.compare?.head ?? null;
  }
  get filesFailure(): Failure | null {
    return this.status === 'error' ? this.failure : null;
  }
  get totals(): { added: number; removed: number } {
    return this.compare?.totals ?? { added: 0, removed: 0 };
  }
  /** Files changed past GitHub's 300 that could not be listed: null when a tree was too large to read, else 0. */
  get filesMissing(): number | null {
    return this.compare?.filesMissing ?? 0;
  }

  get filesLoadView(): LoadView {
    return this.loadView();
  }
  /** The load screen: one step, reading the compare. */
  loadView(): LoadView {
    const label = `Comparing ${this.compare?.baseRef ?? this.subject.base ?? 'the default branch'} with ${compareHead(this.subject)}`;
    return oneStep(subjectLabel(this.subject), label, this.status === 'loading' ? 'loading' : this.status === 'error' && this.failure ? { failure: this.failure, what: 'this compare' } : 'ready');
  }

  get serverDataText(): string | null {
    if (this.unrecorded) return `Changeling can't reach ${this.subject.owner}/${this.subject.repo} through an installation it knows about, so it keeps no notes or review marks here. If you just installed it, reload.`;
    return null;
  }
  /** A compare writes nothing to GitHub; only the server's notes, which are off without an installation. */
  get readOnly(): boolean {
    return this.unrecorded;
  }
  get readOnlyText(): string | null {
    return this.unrecorded ? this.serverDataText : null;
  }
  readonly installLink: string | null = $derived(installUrl(session.config?.app_slug, session.config?.github_web_url));

  /**
   * Read the compare (joining a read under way). The first read shows the
   * load screen; a later one (a new head) replaces what is shown when it
   * lands, and leaves it in place when it fails, or when GitHub no longer
   * has the compare (`gone`).
   */
  load(): Promise<void> {
    if (this.loading) return this.loading;
    const first = !this.compare;
    if (first) this.status = 'loading';
    const signal = this.abort.signal;
    const run = (async () => {
      try {
        const c = await this.world.load(this.subject, signal);
        if (this.disposed) return;
        this.lastLoadAt = Date.now();
        const was = this.compare;
        if (!was || was.head !== c.head || was.mergeBase !== c.mergeBase || was.baseSha !== c.baseSha) this.compare = c;
        this.status = 'ready';
        this.failure = null;
        this.gone = null;
        if (!was || was.head !== c.head) this.afterHead(c, signal);
        if (!was || was.baseRef !== c.baseRef) void this.readPulls(c.baseRef, signal);
      } catch (e) {
        if (this.disposed || signal.aborted) return;
        if (e instanceof CompareNotFound && !first) this.gone = e.message;
        else if (e instanceof CompareNotFound) {
          this.notFound = e.message;
          this.status = 'notfound';
        } else if (first) {
          this.failure = failureOf(e);
          this.status = 'error';
        }
      } finally {
        this.loading = null;
      }
    })();
    this.loading = run;
    return run;
  }

  /** Start listening for what changes the compare (see the file comment), and read it. */
  open(): Promise<void> {
    const repo = `${this.subject.owner}/${this.subject.repo}`.toLowerCase();
    const topics = [`repo:${repo}`];
    if (session.user) topics.push(`user:${session.user.id}`);
    this.stop = this.world.listen(topics, (topic, notice) => this.onNotice(topic, notice));
    void this.marks.load();
    return this.load();
  }

  /** A notice, or a trigger (open, reconnect, resync, focus) when `notice` is null. */
  onNotice(topic: string, notice: Notice | null): void {
    if (this.disposed) return;
    if (!notice) {
      const trigger = topic as Trigger;
      // The first read is `open`'s own.
      if (trigger === 'open' || !this.compare || pausedForRateLimit() || !shouldRefetch(trigger, this.lastLoadAt)) return;
      void this.load();
      void this.marks.load();
      void this.notes.reload();
      return;
    }
    const ours = typeof notice.repo === 'string' && notice.repo.toLowerCase() === `${this.subject.owner}/${this.subject.repo}`.toLowerCase();
    if (notice.kind === 'notes' && ours) void this.notes.heard(notice);
    // Marks and flags notices name what changed; the store looks up again what it shows of it.
    else if ((notice.kind === 'marks' || notice.kind === 'flags') && ours) void this.marks.onNotice(notice as MarksNotice);
    else if (notice.kind === 'checks' && this.compare && (!notice.head_sha || notice.head_sha === this.compare.head)) void this.readChecks(this.compare.head, this.abort.signal);
  }

  /** What is read of a new head: its checks and their annotations, `.gitattributes`, and the linked pull requests asked for before. */
  private afterHead(c: LoadedCompare, signal: AbortSignal) {
    void this.readChecks(c.head, signal);
    void this.world.gitattributes(this.subject, c.head, signal).then(
      (text) => {
        if (!this.disposed && this.compare?.head === c.head) this.generated = generatedMatcher(text);
      },
      () => {},
    );
    if (this.linkedFor !== null) void this.loadLinked(true);
  }

  private async readPulls(base: string, signal: AbortSignal) {
    try {
      const pulls = await this.world.pulls(this.subject, base, signal);
      if (!this.disposed) this.pulls = pulls;
    } catch {
      /* no link to a pull request: the page offers to open one */
    }
  }

  private async readChecks(head: string, signal: AbortSignal) {
    let checks: HeadChecks;
    try {
      checks = await this.world.checks(this.subject, head, signal);
    } catch {
      return;
    }
    if (this.disposed || this.compare?.head !== head) return;
    this.checks = checks;
    // Check runs carry annotations (a status has none): completed ones, failing first.
    const annotated = checks.contexts
      .filter((c) => c.__typename === 'CheckRun' && c.status === 'COMPLETED' && typeof c.databaseId === 'number')
      .sort((a, b) => Number(contextFails(b)) - Number(contextFails(a)))
      .slice(0, MAX_ANNOTATED_RUNS);
    const got = await Promise.all(annotated.map((r) => this.world.annotations(this.subject, r, signal).catch(() => [] as CheckAnnotation[])));
    if (this.disposed || this.compare?.head !== head) return;
    const byPath: Record<string, CheckAnnotation[]> = {};
    for (const a of got.flat()) (byPath[a.path] ??= []).push(a);
    this.annotations = byPath;
  }

  /**
   * Read each commit's pull requests (the newest of the track's, as
   * `fetchLinkedPulls` bounds them), once per head; `again` reads them for
   * a new head.
   */
  async loadLinked(again = false): Promise<void> {
    const c = this.compare;
    if (!c || (this.linkedFor === c.head && !again)) return;
    this.linkedFor = c.head;
    try {
      const got = await this.world.linked(this.subject, this.track.main.map((p) => p.sha), this.abort.signal);
      if (!this.disposed && this.compare?.head === c.head) {
        this.linked = got;
        this.linkedError = null;
      }
    } catch (e) {
      if (this.disposed) return;
      this.linkedFor = null;
      this.linkedError = `The pull requests that brought these commits in could not be read: ${e instanceof Error ? e.message : String(e)}`;
    }
  }

  // DiffSource: the files come with the compare, read whole (GitHub lists them on its first page).
  loadFiles(): Promise<void> {
    return this.compare ? Promise.resolve() : this.load();
  }
  filesGrew(): Promise<void> {
    return this.loadFiles();
  }
  showDiff(owner: object, head: string | null): void {
    this.diffOwner = owner;
    this.diffHead = head;
  }
  hideDiff(owner: object): void {
    if (this.diffOwner !== owner) return;
    this.diffOwner = null;
    this.diffHead = null;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abort.abort();
    this.stop();
    this.unwatch();
    this.marks.dispose();
    this.releaseNotes();
  }
}
